import { UNITS, planCost, planSize, type AttackPlan, type UnitKind } from "@empire/core-sim";

/**
 * The three armies the editor throws at your maze (S2-5).
 *
 * They exist to answer one question — "does this defence actually work?" —
 * before you commit it, and to answer it against the three shapes of attack
 * the balance sheet is built around: a swarm, an armoured push, and siege.
 *
 * They are deliberately near-identical in COST. Comparing outcomes between
 * armies of different value tells you nothing: a bigger army winning is not a
 * finding. This is the same mistake that made my first balance run wrong, so
 * a test pins the three totals to within 5% of each other.
 *
 * All three are worth about the same as each other and about the reference army
 * value the sweep is sized against, which D-104 lowered from 5000 to 3500:
 * 3080, 3060 and 3100. That value stopped being a rule at D-109 — an attack now
 * spends the units you trained — so these are a yardstick for the editor's dry
 * run, not an army you are guaranteed to be able to launch. What you can launch
 * is what is standing in your village.
 */

export interface Preset {
  readonly id: string;
  readonly name: string;
  /** One line, in the defender's terms: what this army is trying to do to you. */
  readonly blurb: string;
  readonly plan: AttackPlan;
}

function wave(offsetTicks: number, counts: Partial<Record<UnitKind, number>>): {
  offsetTicks: number;
  units: UnitKind[];
} {
  const units: UnitKind[] = [];
  for (const [kind, n] of Object.entries(counts)) {
    for (let i = 0; i < (n ?? 0); i++) units.push(kind as UnitKind);
  }
  return { offsetTicks, units };
}

export const PRESETS: readonly Preset[] = [
  {
    id: "swarm",
    name: "Swarm",
    blurb: "Twenty-eight Militia, cheap and fast. Punishes single-target towers.",
    plan: {
      waves: [wave(0, { militia: 14 }), wave(50, { militia: 14 })],
    },
  },
  {
    id: "armoured",
    name: "Armoured push",
    blurb: "Shieldbearers soak the damage while Riders run past. Punishes low damage.",
    plan: {
      waves: [wave(0, { shieldbearer: 7 }), wave(40, { rider: 4 })],
    },
  },
  {
    id: "siege",
    name: "Siege",
    blurb: "Rams break barricades, Saboteurs silence towers, Priests heal. Punishes long roads.",
    plan: {
      waves: [wave(0, { ram: 2, saboteur: 2 }), wave(30, { priest: 2 })],
    },
  },
];

/**
 * Cost and size now come from core-sim.
 *
 * They used to be computed here. Once the SERVER had to answer "can this
 * attacker afford this army?" (S5-3), a second copy of the arithmetic became a
 * liability: the editor would have told a defender an army was affordable that
 * the server then refused. Same reason the editor never had its own copy of
 * validateLayout.
 */
export { planCost, planSize };

/** Every unit kind the presets use, in balance-sheet order. */
export function kindsUsed(plan: AttackPlan): { kind: UnitKind; count: number }[] {
  const counts = new Map<UnitKind, number>();
  for (const w of plan.waves) {
    for (const kind of w.units) counts.set(kind, (counts.get(kind) ?? 0) + 1);
  }
  return UNITS.filter((u) => counts.has(u.id as UnitKind)).map((u) => ({
    kind: u.id as UnitKind,
    count: counts.get(u.id as UnitKind)!,
  }));
}
